import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Landmark, CreditCard, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, fmtEUR } from "@/i18n";
import { api } from "@/api";
import { Reveal, Overline } from "@/components/motion";

const CampaignDetail = () => {
  const { id } = useParams();
  const { lang, clang, cf, t } = useLanguage();
  const [campaign, setCampaign] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("card");
  const [bank, setBank] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", amount: 25, anonymous: false });
  const [loading, setLoading] = useState(false);
  const [pledge, setPledge] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [donors, setDonors] = useState([]);

  const shareUrl = window.location.href;
  const shareText = campaign ? `${cf(campaign, "title")} — Future Gen ASBL` : "Future Gen ASBL";
  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("detail.copied"));
    } catch {
      toast.error("Error");
    }
  };

  useEffect(() => {
    api.get(`/campaigns/${id}`).then((r) => setCampaign(r.data)).catch(() => setNotFound(true));
    api.get("/config/public").then((r) => setBank(r.data.bank)).catch(() => {});
    api.get(`/campaigns/${id}/updates`).then((r) => setUpdates(r.data)).catch(() => {});
    api.get(`/campaigns/${id}/donors`).then((r) => setDonors(r.data)).catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="campaign-not-found">
        <p className="font-serif text-4xl text-forest">404</p>
        <Link to="/campaigns" className="text-clay underline mt-4 inline-block">{t("detail.back")}</Link>
      </div>
    );
  }

  if (!campaign) return <div className="pt-40 pb-32 px-6 text-sage" data-testid="campaign-loading">{t("common.loading")}</div>;

  const pct = campaign.goal_amount > 0 ? Math.min(100, Math.round((campaign.raised_amount / campaign.goal_amount) * 100)) : 0;
  const purposes = cf(campaign, "purpose") || [];
  const past = campaign.status === "past";

  const startStripe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "donation_custom",
        origin_url: window.location.origin,
        name: form.name,
        email: form.email,
        campaign_id: campaign.id,
        anonymous: form.anonymous,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
      setLoading(false);
    }
  };

  const submitPledge = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/donations/pledge", {
        campaign_id: campaign.id,
        name: form.name,
        email: form.email,
        amount: Number(form.amount),
        anonymous: form.anonymous,
      });
      setPledge(data);
      toast.success(t("detail.pledgeThanks"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="campaign-detail-page" className="pt-24">
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pt-10">
        <Link to="/campaigns" data-testid="back-to-campaigns" className="inline-flex items-center gap-2 text-sage hover:text-clay transition-colors duration-300 text-sm font-medium">
          <ArrowLeft size={16} /> {t("detail.back")}
        </Link>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <Reveal>
            <div className="relative overflow-hidden editorial-frame">
              <img src={campaign.image_url} alt={cf(campaign, "title")} data-testid="campaign-image" className="w-full aspect-[16/10] object-cover" />
              {past && (
                <span className="absolute top-6 left-6 bg-forest text-cream text-[10px] uppercase tracking-[0.2em] font-semibold px-4 py-1.5 rounded-full">
                  {t("campaignsPage.completed")}
                </span>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="inline-block mt-10 text-[10px] uppercase tracking-[0.2em] font-semibold text-clay">{campaign.category}</span>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-forest" data-testid="campaign-title">
              {cf(campaign, "title")}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-base lg:text-lg leading-relaxed text-sage" data-testid="campaign-description">
              {cf(campaign, "description")}
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-12 border border-forest/15 p-8 bg-cream" data-testid="campaign-purpose">
              <Overline>{t("detail.purpose")}</Overline>
              <ul className="flex flex-col gap-4">
                {purposes.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-forest" data-testid={`purpose-item-${i}`}>
                    <Check size={18} className="text-clay mt-0.5 shrink-0" />
                    <span className="text-base">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          {updates.length > 0 && (
            <Reveal delay={0.3}>
              <div className="mt-14" data-testid="campaign-updates">
                <Overline>{t("detail.updates")}</Overline>
                <div className="flex flex-col gap-10">
                  {updates.map((u) => (
                    <article key={u.id} className="border-l-2 border-clay/40 pl-6" data-testid={`campaign-update-${u.id}`}>
                      <p className="text-xs uppercase tracking-[0.15em] text-sage">
                        {new Date(u.created_at).toLocaleDateString({ fr: "fr-BE", de: "de-DE", tr: "tr-TR" }[lang] || "en-GB", { day: "2-digit", month: "long", year: "numeric" })}
                      </p>
                      <h3 className="mt-2 font-serif text-2xl text-forest">{cf(u, "title")}</h3>
                      {u.image_url && <img src={u.image_url} alt={cf(u, "title")} className="mt-4 w-full max-h-80 object-cover editorial-frame" />}
                      {cf(u, "body") && <p className="mt-3 text-base leading-relaxed text-sage whitespace-pre-wrap">{cf(u, "body")}</p>}
                    </article>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="lg:sticky lg:top-28 border border-forest/15 bg-cream p-8 md:p-10" data-testid="donation-card">
              <div className="mb-8">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="font-serif text-4xl md:text-5xl font-semibold text-forest" data-testid="campaign-raised">{fmtEUR(campaign.raised_amount, lang)}</p>
                  <p className="text-sm text-sage">{t("detail.raisedOf")} {fmtEUR(campaign.goal_amount, lang)}</p>
                </div>
                <div className="mt-4 h-1.5 bg-forest/10 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${past ? "bg-moss" : "bg-clay"}`} style={{ width: `${pct}%` }} />
                </div>
                <p className="mt-2 text-xs text-sage" data-testid="campaign-percent">{pct}%</p>
              </div>

              {!past && (
                <>
                  <h2 className="font-serif text-2xl text-forest mb-6">{t("detail.donateTitle")}</h2>
                  <div className="flex border border-forest/15 rounded-full overflow-hidden mb-8">
                    <button
                      data-testid="donate-tab-card"
                      onClick={() => setTab("card")}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors duration-300 ${tab === "card" ? "bg-forest text-cream" : "text-forest hover:bg-forest/5"}`}
                    >
                      <CreditCard size={16} /> {t("detail.cardTab")}
                    </button>
                    <button
                      data-testid="donate-tab-bank"
                      onClick={() => setTab("bank")}
                      className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors duration-300 ${tab === "bank" ? "bg-forest text-cream" : "text-forest hover:bg-forest/5"}`}
                    >
                      <Landmark size={16} /> {t("detail.bankTab")}
                    </button>
                  </div>

                  {tab === "card" && (
                    <form onSubmit={startStripe} className="flex flex-col gap-4" data-testid="stripe-donate-form">
                      <input
                        data-testid="donor-name-input"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder={t("detail.name")}
                        className="w-full bg-transparent border border-forest/20 rounded-full px-5 py-3 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300"
                      />
                      <input
                        data-testid="donor-email-input"
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder={t("detail.email")}
                        className="w-full bg-transparent border border-forest/20 rounded-full px-5 py-3 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300"
                      />
                      <p className="text-xs text-sage leading-relaxed">{t("detail.stripeNote")}</p>
                      <label className="flex items-start gap-2.5 cursor-pointer text-sm text-forest" data-testid="anonymous-label-card">
                        <input
                          data-testid="anonymous-checkbox-card"
                          type="checkbox"
                          checked={form.anonymous}
                          onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                          className="mt-0.5 h-4 w-4 accent-[#C4622D] cursor-pointer"
                        />
                        <span className="leading-snug">{t("detail.anonymousLabel")}</span>
                      </label>
                      <button
                        data-testid="stripe-donate-submit"
                        disabled={loading}
                        className="mt-2 inline-flex items-center justify-center gap-2 bg-clay text-cream px-8 py-4 rounded-full font-medium hover:bg-clay-hover hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60"
                      >
                        {t("detail.donateNow")} <ArrowUpRight size={18} />
                      </button>
                    </form>
                  )}

                  {tab === "bank" && !pledge && (
                    <form onSubmit={submitPledge} className="flex flex-col gap-4" data-testid="bank-pledge-form">
                      <p className="text-xs text-sage leading-relaxed">{t("detail.bankInfo")}</p>
                      <input
                        data-testid="pledge-name-input"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder={t("detail.name")}
                        className="w-full bg-transparent border border-forest/20 rounded-full px-5 py-3 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300"
                      />
                      <input
                        data-testid="pledge-email-input"
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder={t("detail.email")}
                        className="w-full bg-transparent border border-forest/20 rounded-full px-5 py-3 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300"
                      />
                      <input
                        data-testid="pledge-amount-input"
                        required
                        type="number"
                        min="1"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        placeholder={t("detail.amount")}
                        className="w-full bg-transparent border border-forest/20 rounded-full px-5 py-3 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300"
                      />
                      <label className="flex items-start gap-2.5 cursor-pointer text-sm text-forest" data-testid="anonymous-label-bank">
                        <input
                          data-testid="anonymous-checkbox-bank"
                          type="checkbox"
                          checked={form.anonymous}
                          onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                          className="mt-0.5 h-4 w-4 accent-[#C4622D] cursor-pointer"
                        />
                        <span className="leading-snug">{t("detail.anonymousLabel")}</span>
                      </label>
                      <button
                        data-testid="pledge-submit"
                        disabled={loading}
                        className="mt-2 inline-flex items-center justify-center gap-2 bg-forest text-cream px-8 py-4 rounded-full font-medium hover:bg-clay hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60"
                      >
                        {t("detail.pledgeBtn")} <ArrowUpRight size={18} />
                      </button>
                    </form>
                  )}

                  {tab === "bank" && pledge && bank && (
                    <div className="border border-moss/30 bg-moss/5 p-6" data-testid="pledge-confirmation">
                      <p className="text-sm text-forest font-medium mb-4">{t("detail.pledgeThanks")}</p>
                      <div className="flex flex-col gap-2 text-sm text-sage">
                        <p><span className="font-semibold text-forest">{t("detail.reference")}:</span> <span data-testid="pledge-reference" className="font-mono text-clay">{pledge.reference}</span></p>
                        <p><span className="font-semibold text-forest">{t("detail.iban")}:</span> {bank.iban}</p>
                        <p><span className="font-semibold text-forest">{t("detail.bic")}:</span> {bank.bic}</p>
                        <p><span className="font-semibold text-forest">{t("detail.accountName")}:</span> {bank.account_name}</p>
                        <p><span className="font-semibold text-forest">{t("detail.amount")}:</span> {fmtEUR(pledge.amount, lang)}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex items-center gap-3 flex-wrap" data-testid="share-section">
              <span className="text-xs uppercase tracking-[0.2em] text-sage mr-1">{t("detail.share")}</span>
              <a data-testid="share-whatsapp" href={waUrl} target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp" className="w-11 h-11 rounded-full border border-forest/20 flex items-center justify-center text-forest hover:bg-[#25D366] hover:border-[#25D366] hover:text-white transition-colors duration-300">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              <a data-testid="share-facebook" href={fbUrl} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className="w-11 h-11 rounded-full border border-forest/20 flex items-center justify-center text-forest hover:bg-[#1877F2] hover:border-[#1877F2] hover:text-white transition-colors duration-300">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <button data-testid="share-copy" onClick={copyLink} aria-label="Copy link" className="w-11 h-11 rounded-full border border-forest/20 flex items-center justify-center text-forest hover:bg-forest hover:text-cream transition-colors duration-300">
                <Link2 size={18} />
              </button>
            </div>
          </Reveal>
          {donors.length > 0 && (
            <Reveal delay={0.25}>
              <div className="mt-8 border border-forest/10 bg-white/60 p-8" data-testid="donor-wall">
                <Overline>{t("detail.donorWall")}</Overline>
                <div className="flex flex-col gap-3">
                  {donors.map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-sm" data-testid={`donor-wall-item-${i}`}>
                      <span className={`flex items-center gap-3 ${d.anonymous ? "text-sage italic" : "text-forest"}`}>
                        <span className="w-8 h-8 rounded-full bg-clay text-cream font-serif flex items-center justify-center">
                          {d.anonymous ? "•" : d.name.charAt(0).toUpperCase()}
                        </span>
                        {d.anonymous ? t("detail.anonymous") : d.name}
                      </span>
                      <span className="font-medium text-clay">{fmtEUR(d.amount, lang)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
};

export default CampaignDetail;
